import { useQueryState } from 'nuqs';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiErrorMessage, apiPublic } from '@/lib/api';
import { PATHS } from '@/lib/paths';

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [token] = useQueryState('token', { defaultValue: '' });

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('Invalid reset link. Please request a new one.');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      await apiPublic(PATHS.authResetPassword, {
        method: 'POST',
        body: { token, newPassword },
      });
      setSuccess(true);
    } catch (e) {
      setError(apiErrorMessage(e, 'Failed to reset password.'));
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-[400px]">
        <div className="rounded-2xl border border-border bg-card shadow-2xl overflow-hidden">
          <div className="px-8 pt-8 pb-4 text-center">
            <h2 className="text-xl font-semibold text-foreground tracking-tight">
              {success ? 'Password Reset' : 'Set New Password'}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {success ? 'Your password has been updated.' : 'Enter your new password below.'}
            </p>
          </div>

          {success ? (
            <div className="px-8 pb-8 flex flex-col gap-4">
              <div className="text-xs px-3 py-2 rounded-lg bg-green-500/10 text-green-400 border border-green-500/20">
                Password reset successfully. You can now sign in with your new password.
              </div>
              <Button
                onClick={() => navigate('/login')}
                className="w-full h-10 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-lg transition-colors"
              >
                Go to Sign In
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="px-8 pb-8 flex flex-col gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="new-pass" className="text-xs text-muted-foreground font-medium">
                  New Password
                </Label>
                <Input
                  id="new-pass"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  autoFocus
                  className="bg-white/[0.04] border-border text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:ring-primary/20 h-10"
                  placeholder="••••••••"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="confirm-pass" className="text-xs text-muted-foreground font-medium">
                  Confirm Password
                </Label>
                <Input
                  id="confirm-pass"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  className="bg-white/[0.04] border-border text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:ring-primary/20 h-10"
                  placeholder="••••••••"
                />
              </div>

              {error && (
                <div className="text-xs px-3 py-2 rounded-lg bg-destructive/10 text-destructive border border-destructive/20">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-10 bg-primary hover:bg-primary/90 text-primary-foreground font-medium mt-1 rounded-lg transition-colors"
              >
                {submitting ? 'Please wait...' : 'Reset Password'}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  Back to sign in
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
